import { CheckCircle2, LoaderCircle, Send } from "lucide-react";
import { FormEvent, useEffect, useRef, useState } from "react";

type FormStatus = "idle" | "submitting" | "success" | "error";

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

export function ContactForm() {
  const isCvRequest = new URLSearchParams(window.location.search).get("request") === "cv";
  const [status, setStatus] = useState<FormStatus>("idle");
  const [message, setMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!siteKey || !turnstileRef.current) return;
    const scriptId = "cf-turnstile-script";
    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current || turnstileRef.current.childElementCount) return;
      window.turnstile.render(turnstileRef.current, {
        sitekey: siteKey,
        theme: "auto",
        action: "contact",
        callback: (token: string) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      });
    };

    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (existing) {
      if (window.turnstile) renderWidget();
      else existing.addEventListener("load", renderWidget, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", renderWidget, { once: true });
    document.head.appendChild(script);
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    if (siteKey && !turnstileToken) {
      setStatus("error");
      setMessage("Please complete the verification before sending.");
      return;
    }

    setStatus("submitting");
    setMessage("");
    const data = new FormData(form);
    const payload = Object.fromEntries(data.entries());
    payload.turnstileToken = turnstileToken;

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const result = (await response.json()) as { message?: string };
      if (!response.ok) throw new Error(result.message || "The message could not be sent.");
      setStatus("success");
      setMessage(result.message || "Thanks—your message was submitted successfully.");
      form.reset();
      setTurnstileToken("");
      window.turnstile?.reset();
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Something went wrong. Please email me directly instead.");
      window.turnstile?.reset();
      setTurnstileToken("");
    }
  }

  return (
    <form className="contact-form panel" onSubmit={handleSubmit} noValidate>
      <div className="form-row">
        <label>Name<input name="name" type="text" autoComplete="name" required minLength={2} maxLength={80} /></label>
        <label>Email<input name="email" type="email" autoComplete="email" required maxLength={160} /></label>
      </div>
      <label>Subject<input name="subject" type="text" required minLength={3} maxLength={120} defaultValue={isCvRequest ? "CV request" : ""} /></label>
      <label>Message<textarea name="message" required minLength={20} maxLength={4000} rows={6} defaultValue={isCvRequest ? "Hello Yuddhish, I would like to request a copy of your CV." : ""} /></label>
      <label className="honeypot" aria-hidden="true">Company website<input name="website" type="text" tabIndex={-1} autoComplete="off" /></label>
      {siteKey ? <div ref={turnstileRef} className="turnstile-container" /> : <p className="form-config-note">Turnstile activates when its public site key is configured.</p>}
      <div className="form-submit-row">
        <button className="button button-primary" type="submit" disabled={status === "submitting"}>
          {status === "submitting" ? <><LoaderCircle className="spin" aria-hidden="true" /> Sending…</> : <>Send message <Send aria-hidden="true" /></>}
        </button>
        {status === "success" && <CheckCircle2 className="success-icon" aria-hidden="true" />}
        <p className={`form-status ${status === "error" ? "error" : ""}`} role="status" aria-live="polite">{message}</p>
      </div>
    </form>
  );
}
